import { Alert, AlertDescription } from '@ophelos/ui/components/alert';
import { Badge } from '@ophelos/ui/components/badge';
import { Button } from '@ophelos/ui/components/button';
import { Card, CardContent } from '@ophelos/ui/components/card';
import { Separator } from '@ophelos/ui/components/separator';
import { Clock } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { BottomActionBar } from '@/components/layout/BottomActionBar';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import { useTranslation } from '@/hooks/useTranslation';
import type { OfferTerms, ReferredOutcome } from '@/hooks/useOfferGate';
import { toSubunits } from '@/lib/currency';

interface OfferOutcomeProps {
  /**
   * Which referral this was. It selects nothing on screen — see the note on the component — but
   * the caller still passes it so the type stays honest about what reached this screen.
   */
  outcome: ReferredOutcome;
  /** The terms the verdict was taken on, not live state. */
  terms: OfferTerms;
  currency: string;
  /** The frequency label the plan tab already renders, so both screens word it identically. */
  frequencyLabel: string;
  /** Back to the slider to try different numbers. */
  onChoosePlan: () => void;
  onDone: () => void;
  isBusy?: boolean;
  error?: string | null;
}

/**
 * The customer-facing verdict on a submitted offer that a human has to look at.
 *
 * One screen for every referral, because the record does not distinguish them: an
 * `ops_approval` and a flagged situation are both `referred` with `status = 'pending'` on
 * `payments.plan_requests`, reviewed by the same person through the same surface. Two screens
 * would be two promises where the system makes one, and the wording would drift apart.
 *
 * Deliberately says nothing about *why* an offer was referred. The server never sends a
 * reason — a flagged situation and an agent-recorded vulnerability mark both arrive as
 * `contact_us` — and naming either would disclose what we hold about someone from a screen
 * they reached by offering to pay.
 *
 * An accepted offer never lands here: `useOfferGate` returns it as `{ go: 'checkout' }`, so the
 * customer goes on to review or setup rather than reading that their plan is ready to set up
 * immediately before the screen that sets it up.
 */
export function OfferOutcome({
  terms,
  currency,
  frequencyLabel,
  onChoosePlan,
  onDone,
  isBusy = false,
  error = null,
}: OfferOutcomeProps) {
  const { t } = useTranslation();
  const { config } = useConfiguration();
  const supportUrl = config.ui.companyInfo?.supportUrl;
  const heading = useRef<HTMLHeadingElement>(null);

  // Swapping the tab body moves nothing for a keyboard or screen-reader user.
  useEffect(() => heading.current?.focus(), []);

  // The figure the offer was submitted with, rounded the same way, or the screen quotes a cent
  // less than the amount a human is being asked to review.
  const perPayment = Math.ceil(toSubunits(terms.totalAmount) / terms.count) / 100;

  return (
    <div className="w-full h-full stack items-center px-4">
      <div className="flex-1 w-full max-w-sm mx-auto space-y-6 pt-6">
        <div className="space-y-2">
          <div className="flex items-start gap-2 min-w-0">
            <Clock className="size-5 text-primary shrink-0 mt-1.5" />
            <h1
              ref={heading}
              tabIndex={-1}
              className="text-2xl font-bold text-foreground focus-visible:outline-none"
            >
              {t('paymentOffer.referred.title')}
            </h1>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">{t('paymentOffer.referred.body')}</p>
        </div>

        {/* Frequency, count and instalment only — never the balance. The instalment is derived
            from a discounted total while the server priced against the ledger, so the two
            figures do not reconcile and showing both invites a question nobody can answer. */}
        <Card>
          <CardContent className="text-center py-6">
            <p className="text-sm text-muted-foreground mb-2">{t('paymentOffer.perPayment')}</p>
            <CurrencyDisplay amount={perPayment} currency={currency} className="text-4xl" centsClassName="text-2xl" />
            <Separator className="my-4" />
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {t('paymentOffer.instalments', { count: terms.count, frequency: frequencyLabel })}
              </span>
              <Badge variant="caution">{t('paymentOffer.referred.status')}</Badge>
            </div>
          </CardContent>
        </Card>

        {/* A whole sentence rather than a link spliced into one: word order differs by language,
            and only some clients configure a support URL to link to at all. */}
        <Alert variant="info">
            <AlertDescription>
            {supportUrl ? (
              <a href={supportUrl} target="_blank" rel="noreferrer" className="font-medium underline underline-offset-2">
                {t('paymentOffer.contactUsLink')}
              </a>
            ) : (
              t('paymentOffer.contactUsPlain')
            )}
          </AlertDescription>
        </Alert>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>

      <BottomActionBar>
        <Button size="xl" className="w-full max-w-sm" onClick={onDone} disabled={isBusy}>
          {t('paymentOffer.backToDashboard')}
        </Button>
        <Button variant="ghost" size="sm" className="w-full max-w-sm" onClick={onChoosePlan} disabled={isBusy}>
          {t('paymentOffer.choosePlan')}
        </Button>
      </BottomActionBar>
    </div>
  );
}
